@if (schoolUnitOffersOnboardingLayout()) {
  <app-school-unit-offers
    [showNegotiatedOffer]="showNegotiatedOffer()"
    (selectOffer)="relaySchoolUnitOnboardingOffer($event)" />
} @else if (role()) {
  <!-- ═══════════════════════════════════════════════════════════════════
     Modern single-card view (same as onboarding)
     ═══════════════════════════════════════════════════════════════════ -->
  <div class="min-h-min w-full pb-8">
    <div class="mx-auto flex max-w-4xl flex-col items-center px-4 pb-16">
      @if (planOrderError) {
        <div
          class="mb-6 w-full max-w-[56rem] rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left text-sm text-red-900"
          role="alert">
          {{ planOrderError }}
        </div>
      }
      <!-- Hero -->
      <div
        class="mb-10 -mt-9 flex w-full max-w-[56rem] flex-col items-center gap-4 text-center md:mb-14">
        <div
          class="inline-flex items-center gap-2 rounded-full bg-bn-blue-light px-4 py-1.5 text-sm font-medium text-light-primary">
          <mat-icon
            class="!h-[14px] !w-[14px] !text-[14px] text-light-primary"
            >school</mat-icon
          >
          Für Schweizer Schulen
        </div>
        <h1 class="text-page-headline-sm text-slate-900 md:text-page-headline">
          @if (offer()) {
            Ihr persönliches Angebot
          } @else if (role() === 'schulgemeinde') {
            Dein Angebot
          } @else {
            Attraktive Angebote für alle
          }
        </h1>
        <p class="max-w-2xl text-lg font-light text-slate-500">
          @if (offer()) {
            Dieses Angebot wurde für deine {{ planName }} zusammengestellt.
          } @else {
            Wähle das passende Abo für deine {{ planName }}.
          }
        </p>

        @if (!offer()) {
          <!-- Laufzeit -->
          <div class="mt-2 flex justify-center">
            <div
              class="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                class="rounded-md px-6 py-2.5 text-sm font-medium transition-colors"
                [class.bg-light-primary]="selectedDuration === 1"
                [class.text-white]="selectedDuration === 1"
                [class.shadow-sm]="selectedDuration === 1"
                [class.text-slate-600]="selectedDuration !== 1"
                (click)="selectDuration(1)">
                1 Jahr
              </button>
              <button
                type="button"
                class="flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-medium transition-colors"
                [class.bg-light-primary]="selectedDuration === 2"
                [class.text-white]="selectedDuration === 2"
                [class.shadow-sm]="selectedDuration === 2"
                [class.text-slate-600]="selectedDuration !== 2"
                (click)="selectDuration(2)">
                2 Jahre
                <span
                  class="text-[10px] font-bold"
                  [class.text-green-600]="selectedDuration !== 2"
                  [class.text-white]="selectedDuration === 2">
                  Spare 5%
                </span>
              </button>
              <button
                type="button"
                class="flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-medium transition-colors"
                [class.bg-light-primary]="selectedDuration === 3"
                [class.text-white]="selectedDuration === 3"
                [class.shadow-sm]="selectedDuration === 3"
                [class.text-slate-600]="selectedDuration !== 3"
                (click)="selectDuration(3)">
                3 Jahre
                <span
                  class="text-[10px] font-bold"
                  [class.text-green-600]="selectedDuration !== 3"
                  [class.text-white]="selectedDuration === 3">
                  Spare 10%
                </span>
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Pricing Card -->
      @if (offer(); as offer) {
        <article
          data-testid="negotiated-offer-card"
          class="w-full max-w-md overflow-hidden rounded-xl border-2 border-light-primary bg-white shadow-[0_8px_10px_rgba(219,234,254,0.4),0_20px_25px_rgba(219,234,254,0.4)]">
          <div class="bg-bn-blue-light/90 px-6 pb-8 pt-8 text-center">
            <p
              class="m-0 text-xs font-bold uppercase tracking-wider text-light-primary">
              Ihr Angebot
            </p>
            <p class="m-0 mt-2 text-lg font-semibold text-slate-900">
              {{ offer.label }}
            </p>
            <p class="m-0 mt-4 text-2xl font-bold text-light-primary">
              {{ formatRappenChf(offer.yearly_price_rappen) }} pro Jahr
            </p>
          </div>
          <div
            class="space-y-4 px-6 py-8 text-sm leading-relaxed text-slate-600">
            <p class="m-0">
              Mindestlaufzeit {{ offer.contract_years }} Jahre ({{
                offer.contract_start | date: 'dd.MM.yyyy'
              }}
              – {{ offer.contract_end | date: 'dd.MM.yyyy' }}), danach jährliche
              Verlängerung
            </p>
            <p class="m-0">
              Enthalten: Admins {{ limitLabel(offer.user_invitation_left) }} ·
              Schuleinheiten {{ limitLabel(offer.school_unit_creation_left) }} ·
              Standorte {{ limitLabel(offer.sub_school_creation_left) }} ·
              Stelleninserate
              {{ limitLabel(offer.permanent_job_postings_left) }} /
              Stellvertretungen
              {{ limitLabel(offer.substitute_job_postings_left) }}
            </p>
          <app-offer-points [points]="offer.customer_notes" />
            <button
              type="button"
              data-testid="accept-offer"
              class="flex w-full items-center justify-center rounded-lg bg-light-primary py-3.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              (click)="onAcceptOffer()">
              Angebot annehmen
            </button>
          </div>
        </article>
      } @else if (offerLoading()) {
        <div class="flex w-full justify-center py-16">
          <mat-spinner diameter="40" />
        </div>
      } @else {
        <div class="relative w-full max-w-md">
          <div
            class="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[#1e40af] px-4 py-1.5 text-xs font-medium text-white shadow-md">
            Empfohlen
          </div>

          <article
            class="overflow-hidden rounded-xl border-2 border-light-primary bg-white shadow-[0_8px_10px_rgba(219,234,254,0.4),0_20px_25px_rgba(219,234,254,0.4)]">
            <div class="bg-bn-blue-light/90 px-6 pb-8 pt-10 text-center">
              <p
                class="text-xs font-bold uppercase tracking-wider text-light-primary">
                {{ planName }}
              </p>
              <div class="mt-4 flex items-baseline justify-center gap-2">
                <span
                  class="text-5xl font-bold leading-none text-light-primary md:text-6xl"
                  >{{ baseMonthlyChf }}</span
                >
                <span class="text-lg font-medium text-slate-500">CHF</span>
              </div>
              <p class="mt-1 text-xs text-slate-500">pro Monat</p>
              <p
                class="mx-auto mt-4 max-w-xs whitespace-pre-line text-sm font-light leading-relaxed text-slate-500">
                {{ planDescription }}
              </p>
            </div>

            <div class="space-y-6 px-6 py-8">
              <!-- Zusätzliche Admins -->
              <div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-sm text-slate-600">Zusätzliche Admins</span>
                  <div class="flex items-center gap-0">
                    <button
                      type="button"
                      class="flex h-9 w-9 items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                      [disabled]="extraAdmins <= 0"
                      (click)="decrementAdmins()">
                      –
                    </button>
                    <span
                      class="min-w-[2.5rem] text-center text-base font-medium text-slate-900"
                      >{{ extraAdmins }}</span
                    >
                    <button
                      type="button"
                      class="flex h-9 w-9 items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                      [disabled]="extraAdmins >= 50"
                      (click)="incrementAdmins()">
                      +
                    </button>
                  </div>
                </div>
                <p class="mt-1 text-right text-[10px] text-slate-400">
                  {{ adminHint }}
                </p>
              </div>

              <!-- Schuleinheiten (nur Schulgemeinde) -->
              @if (hasSchoolUnits) {
                <div>
                  <div class="flex items-center justify-between gap-4">
                    <span class="text-sm text-slate-600">Schuleinheiten</span>
                    <div class="flex items-center gap-0">
                      <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                        [disabled]="extraSchoolUnits <= 0"
                        (click)="decrementSchoolUnits()">
                        –
                      </button>
                      <span
                        class="min-w-[2.5rem] text-center text-base font-medium text-slate-900"
                        >{{ extraSchoolUnits }}</span
                      >
                      <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                        [disabled]="extraSchoolUnits >= 50"
                        (click)="incrementSchoolUnits()">
                        +
                      </button>
                    </div>
                  </div>
                  <p class="mt-1 text-right text-[10px] text-slate-400">
                    jede weitere 14.90 Fr./Monat
                  </p>
                </div>
              }

              <!-- Preisübersicht -->
              <div
                class="rounded-lg border border-slate-200 bg-white px-4 py-4 text-center shadow-sm">
                <p class="text-xs text-slate-400">Dein Preis:</p>
                <p class="mt-1 text-2xl font-bold text-light-primary">
                  {{ annualTotalChf.toFixed(2) }} CHF
                </p>
                <p class="mt-1 text-[11px] text-slate-400">
                  für {{ selectedDuration * 12 }} Monate (Gesamtpreis)
                </p>
                @if (discountPercent > 0) {
                  <p class="mt-2 text-xs font-medium text-green-600">
                    {{ discountPercent }}% Rabatt ({{
                      selectedDuration
                    }}-Jahres-Abo)
                  </p>
                }
              </div>

              <!-- CTA -->
              <button
                type="button"
                class="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-3.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="planOrderLoading"
                (click)="selectPaid()">
                @if (planOrderLoading) {
                  <mat-spinner diameter="20" />
                }
                Auswählen
              </button>
            </div>
          </article>
        </div>
      }

      <!-- Leistungsübersicht -->
      <section class="mt-14 w-full max-w-4xl">
        <div class="flex flex-col gap-3">
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-6 py-4 text-left shadow-sm"
            (click)="toggleServices()">
            <span class="text-base font-semibold text-slate-700"
              >Leistungsübersicht</span
            >
            <mat-icon
              class="material-symbols-rounded text-slate-600 transition-transform duration-200"
              [class.-scale-y-100]="servicesExpanded">
              expand_more
            </mat-icon>
          </button>

          @if (servicesExpanded) {
            <div
              class="overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-sm">
              <div class="overflow-x-auto">
                <!-- Kopfzeile -->
                <div
                  class="grid min-w-[min(100%,520px)] grid-cols-[minmax(0,1fr)_minmax(7rem,9rem)] border-b border-slate-200 bg-slate-50">
                  <div
                    class="px-5 py-4 text-sm font-semibold tracking-tight text-slate-900 md:px-6">
                    Features
                  </div>
                  <div
                    class="flex items-center justify-center px-3 py-4 text-center text-sm font-bold tracking-tight text-light-primary">
                    {{ planName }}
                  </div>
                </div>

                @for (block of leistungBlocks; track $index) {
                  @switch (block.type) {
                    @case ('employerBanner') {
                      <div
                        class="border-t border-slate-100 bg-gradient-to-r from-blue-50 via-blue-50/90 to-slate-50/40 px-5 py-4 md:px-6">
                        <div class="flex items-center gap-3">
                          <div
                            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-blue-600 shadow-sm">
                            <mat-icon
                              class="employer-offer-banner__star material-symbols-rounded !text-[28px] leading-none text-white"
                              style="position: relative; left: -1px; top: -2px"
                              >star</mat-icon
                            >
                          </div>
                          <div class="min-w-0 space-y-0.5">
                            <p
                              class="text-sm font-bold leading-snug text-blue-900">
                              {{ block.title }}
                            </p>
                            <p class="text-xs leading-snug text-blue-700">
                              {{ block.subtitle }}
                            </p>
                          </div>
                        </div>
                      </div>
                    }
                    @case ('section') {
                      <div
                        class="border-t border-slate-200 bg-slate-50/90 px-5 py-3.5 md:px-6">
                        <p
                          class="text-xs font-bold uppercase tracking-wide text-slate-400">
                          {{ block.title }}
                        </p>
                      </div>
                    }
                    @case ('row') {
                      <div
                        class="grid min-w-[min(100%,520px)] grid-cols-[minmax(0,1fr)_minmax(7rem,9rem)] border-t border-slate-100">
                        <div class="flex flex-col gap-1 px-5 py-4 md:px-6">
                          <span
                            class="text-sm font-medium leading-snug text-slate-700"
                            >{{ block.label }}</span
                          >
                          @if (block.hint) {
                            <span class="text-xs leading-snug text-slate-400">{{
                              block.hint
                            }}</span>
                          }
                        </div>
                        <div
                          class="flex items-center justify-center px-2 py-4 md:px-3">
                          @switch (block.cell.kind) {
                            @case ('check') {
                              <span
                                class="relative inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-green-50">
                                <mat-icon
                                  class="material-symbols-rounded absolute left-1/2 top-1/2 flex !h-[14px] !w-[14px] -translate-x-1/2 -translate-y-1/2 items-center justify-center !text-[14px] !leading-none text-green-600"
                                  >check</mat-icon
                                >
                              </span>
                            }
                            @case ('premium') {
                              <span
                                class="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white shadow-sm"
                                >Premium</span
                              >
                            }
                            @case ('text') {
                              @switch (block.cell.variant) {
                                @case ('blue') {
                                  <span
                                    class="text-center text-sm font-semibold text-light-primary"
                                    >{{ block.cell.text }}</span
                                  >
                                }
                                @case ('muted') {
                                  <span
                                    class="text-center text-xs font-normal leading-snug text-slate-500"
                                    >{{ block.cell.text }}</span
                                  >
                                }
                                @case ('body') {
                                  <span
                                    class="text-center text-sm font-normal text-slate-700"
                                    >{{ block.cell.text }}</span
                                  >
                                }
                              }
                            }
                          }
                        </div>
                      </div>
                    }
                  }
                }
              </div>
            </div>
          }
        </div>
        <p class="mt-6 text-center text-xs text-slate-400">
          Alle Preise verstehen sich inklusive Mehrwertsteuer &bull; Keine
          versteckten Kosten
        </p>
      </section>
    </div>
  </div>
} @else {
  <!-- ═══════════════════════════════════════════════════════════════════
     Legacy two-card view (offers without role)
     ═══════════════════════════════════════════════════════════════════ -->
  <section class="schools">
    <header class="schools__hero">
      <span class="schools__hero__badge">Für Schweizer Schulen</span>
      <h1 class="schools__hero__headline">Attraktive Angebote für alle</h1>
      <p class="schools__hero__description">
        Starte kostenlos oder wähle das passende Abo für deine Schule.
      </p>
    </header>

    <div class="schools__toggle-wrapper">
      <div class="schools__toggle">
        <button
          class="schools__toggle__btn"
          [class.schools__toggle__btn--active]="selectedDuration === 1"
          (click)="selectDuration(1)">
          1 Jahr
        </button>
        <button
          class="schools__toggle__btn"
          [class.schools__toggle__btn--active]="selectedDuration === 2"
          (click)="selectDuration(2)">
          2 Jahre <span class="schools__toggle__btn__badge">Spare 5%</span>
        </button>
        <button
          class="schools__toggle__btn"
          [class.schools__toggle__btn--active]="selectedDuration === 3"
          (click)="selectDuration(3)">
          3 Jahre <span class="schools__toggle__btn__badge">Spare 10%</span>
        </button>
      </div>
    </div>

    <main class="schools__cards">
      @for (plan of plans; track plan.type) {
        <article
          class="schools__card"
          [class.schools__card--recommended]="plan.recommended">
          @if (plan.recommended) {
            <div class="schools__card__badge">Empfohlen</div>
          }
          <h2 class="schools__card__name">{{ plan.name }}</h2>
          <div class="schools__card__price">
            <span class="schools__card__price__amount">{{
              plan.monthlyPrice
            }}</span>
            <span class="schools__card__price__currency">CHF</span>
            <span class="schools__card__price__period">{{
              plan.monthlyPrice === 0 ? 'die Basis' : 'pro Monat'
            }}</span>
          </div>
          <p class="schools__card__description">{{ plan.description }}</p>
          @if (plan.recommended) {
            <div class="schools__card__admins">
              <span class="schools__card__admins__label"
                >Zusätzliche Admins</span
              >
              <div class="schools__card__admins__stepper">
                <button
                  class="schools__card__admins__stepper__btn"
                  (click)="legacyDecrementAdmins()"
                  [disabled]="extraAdmins === 0">
                  –
                </button>
                <span class="schools__card__admins__stepper__value">{{
                  extraAdmins
                }}</span>
                <button
                  class="schools__card__admins__stepper__btn"
                  (click)="legacyIncrementAdmins()"
                  [disabled]="extraAdmins >= 50">
                  +
                </button>
              </div>
              @if (extraAdmins > 0) {
                <span class="schools__card__admins__cost"
                  >{{ (extraAdmins * 9.9).toFixed(2) }} CHF/Monat</span
                >
              }
            </div>
          }
          @if (plan.monthlyPrice > 0) {
            <div class="schools__card__summary">
              <span class="schools__card__summary__label">Jahresübersicht</span>
              @if (hasDiscount(plan)) {
                <span class="schools__card__summary__original"
                  >{{ (legacyTotalMonthlyPrice * 12).toFixed(2) }} CHF /
                  Jahr</span
                >
              }
              <span class="schools__card__summary__discounted"
                >Dein Preis: {{ legacyTotalAnnualPrice.toFixed(2) }} CHF für 12
                Monate</span
              >
              <span class="schools__card__summary__duration">
                {{ selectedDuration }}-Jahres-Abo
                @if (hasDiscount(plan)) {
                  <span> ({{ discountPercent }}% Rabatt)</span>
                }
              </span>
            </div>
          }
          <button
            class="schools__card__cta"
            [class.schools__card__cta--primary]="plan.recommended"
            (click)="select(plan.type)">
            {{ plan.ctaLabel }}
          </button>
        </article>
      }
    </main>

    <section class="schools__features">
      <button class="schools__features__toggle" (click)="toggleFeatures()">
        <h2 class="schools__features__headline">Leistungsübersicht</h2>
        <mat-icon
          class="material-symbols-rounded schools__features__toggle__icon"
          [class.schools__features__toggle__icon--expanded]="featuresExpanded"
          >expand_more</mat-icon
        >
      </button>
      @if (featuresExpanded) {
        <div class="schools__features__card">
          <div class="schools__features__table-wrapper">
            <table class="schools__features__table">
              <thead>
                <tr>
                  <th class="schools__features__table__sticky">Features</th>
                  @for (name of planNames; track name; let i = $index) {
                    <th [class.schools__features__th--blue]="i === 1">
                      {{ name }}
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (section of featureSections; track section.title) {
                  @if (section.icon) {
                    <tr class="schools__features__table__section-banner">
                      <td [attr.colspan]="planNames.length + 1">
                        <div class="schools__features__banner">
                          <mat-icon
                            class="material-symbols-rounded schools__features__banner__icon"
                            >{{ section.icon }}</mat-icon
                          >
                          <div class="schools__features__banner__text">
                            <strong>{{ section.title }}</strong>
                            @if (section.subtitle) {
                              <span>{{ section.subtitle }}</span>
                            }
                          </div>
                        </div>
                      </td>
                    </tr>
                  } @else {
                    <tr class="schools__features__table__section-header">
                      <td [attr.colspan]="planNames.length + 1">
                        {{ section.title }}
                      </td>
                    </tr>
                  }
                  @for (row of section.rows; track row.label) {
                    <tr>
                      <td class="schools__features__table__sticky">
                        <span class="schools__features__label">{{
                          row.label
                        }}</span>
                        @if (row.hint) {
                          <span class="schools__features__hint">{{
                            row.hint
                          }}</span>
                        }
                      </td>
                      @for (
                        value of row.values;
                        track $index;
                        let ci = $index
                      ) {
                        <td>
                          @if (isCheckOrCross(value) === 'check') {
                            <mat-icon
                              class="material-symbols-rounded schools__features__icon--check"
                              >check</mat-icon
                            >
                          } @else if (isCheckOrCross(value) === 'cross') {
                            <mat-icon
                              class="material-symbols-rounded schools__features__icon--cross"
                              >close</mat-icon
                            >
                          } @else if (isBadge(value)) {
                            <span
                              class="schools__features__badge"
                              [class.schools__features__badge--light]="ci === 0"
                              [class.schools__features__badge--medium]="
                                ci === 1
                              "
                              >{{ value }}</span
                            >
                          } @else if (isBlueText(value)) {
                            <span class="schools__features__blue">{{
                              value
                            }}</span>
                          } @else if (isDash(value)) {
                            <span class="schools__features__dash">{{
                              value
                            }}</span>
                          } @else {
                            <span>{{ value }}</span>
                          }
                        </td>
                      }
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    </section>
  </section>
}
